import { SEO } from '@/components/shared/SEO';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Zap,
  Shield,
  BarChart3,
  Globe,
  Terminal,
  Webhook,
  RotateCcw,
  LayoutDashboard,
  ArrowUpRight,
  Lock,
  Activity,
  ChevronRight,
  Cloud,
  Server,
  RefreshCw,
  ShieldCheck,
  Layers,
} from 'lucide-react';
import { useEffect, useState, useRef, useMemo } from 'react';
import type { AuthUser as User } from '@linabase/js';
import { useInView } from '@/hooks/useInView';
import { Reveal } from '@/components/shared/Reveal';
import { PublicNav } from '@/components/shared/PublicNav';
import { PublicFooter } from '@/components/shared/PublicFooter';
import { useSystemStatus } from '@/hooks/useSystemStatus';
import { ProviderIcon } from '@/components/dashboard/ProviderIcon';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { usePlans } from '@/hooks/usePlan';
import { Check, X } from 'lucide-react';
import { Plan } from '@/types';

/* ── Typewriter effect for terminal ── */
function Typewriter({
  lines,
  className = '',
}: {
  lines: { text: string; color?: string; prefix?: string; delay: number }[];
  className?: string;
}) {
  const [displayedLines, setDisplayedLines] = useState<
    { text: string; color?: string; prefix?: string; typing: boolean }[]
  >([]);
  const hasRunRef = useRef(false);
  const { ref, visible } = useInView(0.3);

  useEffect(() => {
    if (!visible || hasRunRef.current) return;
    hasRunRef.current = true;

    const timeouts: ReturnType<typeof setTimeout>[] = [];
    lines.forEach((line, i) => {
      // Show line start
      timeouts.push(
        // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout
        setTimeout(() => {
          setDisplayedLines((prev) => [
            ...prev,
            { text: '', color: line.color, prefix: line.prefix, typing: true },
          ]);
        }, line.delay)
      );

      // Type each character
      const chars = line.text.split('');
      chars.forEach((char, ci) => {
        timeouts.push(
          // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout
          setTimeout(
            () => {
              setDisplayedLines((prev) => {
                const next = [...prev];
                next[i] = { ...next[i], text: next[i].text + char };
                return next;
              });
            },
            line.delay + 20 + ci * 28
          )
        );
      });

      // Done typing
      timeouts.push(
        // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout
        setTimeout(
          () => {
            setDisplayedLines((prev) => {
              const next = [...prev];
              next[i] = { ...next[i], typing: false };
              return next;
            });
          },
          line.delay + 20 + chars.length * 28 + 100
        )
      );
    });

    return () => timeouts.forEach(clearTimeout);
  }, [visible, lines]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ minHeight: `${lines.length * 1.75}em` }}
    >
      {/* eslint-disable @eslint-react/no-array-index-key */}
      {displayedLines.map((line, i) => (
        <p
          key={i}
          className="leading-relaxed whitespace-nowrap overflow-hidden text-ellipsis text-[10px] sm:text-sm"
          style={{ color: line.color }}
        >
          {line.prefix && <span className="text-primary">{line.prefix}</span>}
          {line.text}
          {line.typing && (
            <span className="typewriter-cursor text-primary ml-0.5">|</span>
          )}
        </p>
      ))}
      {/* eslint-enable @eslint-react/no-array-index-key */}
    </div>
  );
}

/* ── Animated connection SVG ── */
function ConnectionDiagram() {
  const { ref, visible } = useInView(0.2);

  return (
    <div ref={ref} className="relative w-full max-w-md mx-auto">
      <svg viewBox="0 0 400 200" className="w-full" fill="none">
        {/* Connection lines */}
        <path
          d="M 60 60 C 140 60 140 100 200 100 C 260 100 260 140 340 140"
          stroke="hsl(var(--primary))"
          strokeWidth="2"
          className={visible ? 'data-flow-line' : ''}
          opacity="0.6"
        />
        <path
          d="M 60 100 C 120 100 120 100 200 100 C 280 100 280 100 340 100"
          stroke="hsl(var(--primary))"
          strokeWidth="2"
          className={visible ? 'data-flow-line' : ''}
          opacity="0.4"
          style={{ animationDelay: '0.5s' }}
        />
        <path
          d="M 60 140 C 140 140 140 100 200 100 C 260 100 260 60 340 60"
          stroke="hsl(var(--status-success))"
          strokeWidth="2"
          className={visible ? 'data-flow-line-reverse' : ''}
          opacity="0.6"
          style={{ animationDelay: '1s' }}
        />

        {/* Left nodes - webhook sources */}
        <rect
          x="20"
          y="48"
          width="40"
          height="24"
          rx="4"
          fill="hsl(var(--primary))"
          opacity="0.15"
          stroke="hsl(var(--primary))"
          strokeWidth="1"
        />
        <text
          x="40"
          y="64"
          textAnchor="middle"
          fill="hsl(var(--primary))"
          fontSize="8"
          fontFamily="IBM Plex Mono"
        >
          POST
        </text>

        <rect
          x="20"
          y="88"
          width="40"
          height="24"
          rx="4"
          fill="hsl(var(--primary))"
          opacity="0.15"
          stroke="hsl(var(--primary))"
          strokeWidth="1"
        />
        <text
          x="40"
          y="104"
          textAnchor="middle"
          fill="hsl(var(--primary))"
          fontSize="8"
          fontFamily="IBM Plex Mono"
        >
          POST
        </text>

        <rect
          x="20"
          y="128"
          width="40"
          height="24"
          rx="4"
          fill="hsl(var(--status-success))"
          opacity="0.15"
          stroke="hsl(var(--status-success))"
          strokeWidth="1"
        />
        <text
          x="40"
          y="144"
          textAnchor="middle"
          fill="hsl(var(--status-success))"
          fontSize="8"
          fontFamily="IBM Plex Mono"
        >
          GET
        </text>

        {/* Center hub */}
        <circle
          cx="200"
          cy="100"
          r="20"
          fill="hsl(var(--primary))"
          opacity="0.1"
          stroke="hsl(var(--primary))"
          strokeWidth="1.5"
        />
        <circle
          cx="200"
          cy="100"
          r="8"
          fill="hsl(var(--primary))"
          opacity={visible ? '0.6' : '0'}
          style={{ transition: 'opacity 1s ease 0.5s' }}
        >
          <animate
            attributeName="r"
            values="6;10;6"
            dur="2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.6;0.2;0.6"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Right nodes - local targets */}
        <rect
          x="340"
          y="48"
          width="40"
          height="24"
          rx="4"
          fill="hsl(var(--status-success))"
          opacity="0.15"
          stroke="hsl(var(--status-success))"
          strokeWidth="1"
        />
        <text
          x="360"
          y="64"
          textAnchor="middle"
          fill="hsl(var(--status-success))"
          fontSize="7"
          fontFamily="IBM Plex Mono"
        >
          :3000
        </text>

        <rect
          x="340"
          y="88"
          width="40"
          height="24"
          rx="4"
          fill="hsl(var(--status-success))"
          opacity="0.15"
          stroke="hsl(var(--status-success))"
          strokeWidth="1"
        />
        <text
          x="360"
          y="104"
          textAnchor="middle"
          fill="hsl(var(--status-success))"
          fontSize="7"
          fontFamily="IBM Plex Mono"
        >
          :8080
        </text>

        <rect
          x="340"
          y="128"
          width="40"
          height="24"
          rx="4"
          fill="hsl(var(--primary))"
          opacity="0.15"
          stroke="hsl(var(--primary))"
          strokeWidth="1"
        />
        <text
          x="360"
          y="144"
          textAnchor="middle"
          fill="hsl(var(--primary))"
          fontSize="7"
          fontFamily="IBM Plex Mono"
        >
          :5173
        </text>

        {/* Labels */}
        <text
          x="40"
          y="36"
          textAnchor="middle"
          fill="hsl(var(--muted-foreground))"
          fontSize="9"
          fontFamily="IBM Plex Mono"
          opacity="0.7"
        >
          INTERNET
        </text>
        <text
          x="200"
          y="78"
          textAnchor="middle"
          fill="hsl(var(--primary))"
          fontSize="8"
          fontFamily="IBM Plex Mono"
          fontWeight="600"
        >
          HOOKLINK
        </text>
        <text
          x="360"
          y="36"
          textAnchor="middle"
          fill="hsl(var(--muted-foreground))"
          fontSize="9"
          fontFamily="IBM Plex Mono"
          opacity="0.7"
        >
          LOCALHOST
        </text>
      </svg>
    </div>
  );
}

/* ── Main Landing Page ── */

export default function LandingPage() {
  // Check localStorage synchronously for a "probably logged in" hint,
  // then lazily confirm via linabase if a token exists.
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(() => {
    try {
      return Object.keys(localStorage).some((k) => k.startsWith('sb-') && k.endsWith('-auth-token'));
    } catch {
      return false;
    }
  });
  const [cliVersion, setCliVersion] = useState<string | null>(null);
  const { data: plans } = usePlans();

  useEffect(() => {
    // Only import linabase if localStorage suggests a session exists
    if (!loading) return;
    let cancelled = false;
    import('@/lib/linabase').then(({ linabase }) => {
      linabase.auth.getSession().then(({ data: { session } }) => {
        if (cancelled) return;
        setUser(session?.user ?? null);
        setLoading(false);
      });
      const subscription = linabase.auth.onAuthStateChange((_event, session) => {
        if (!cancelled) setUser(session?.user ?? null);
      });
      if (!cancelled) {
        cleanupRef.current = () => subscription.unsubscribe();
      } else {
        subscription.unsubscribe();
      }
    });
    const cleanupRef = { current: () => {} };
    return () => {
      cancelled = true;
      cleanupRef.current();
    };
  }, [loading]);

  useEffect(() => {
    fetch('https://registry.npmjs.org/@hooklink/cli/latest')
      .then((res) => res.json())
      .then((data) => setCliVersion(data.version ?? null))
      .catch(() => {});
  }, []);

  const terminalLines = useMemo(
    () => [
      { text: ' npx @hooklink/cli listen 3000', prefix: '$ ', delay: 300 },
      {
        text: ' Listener ready! abc123.hooklink.net → localhost:3000',
        color: 'hsl(152, 60%, 45%)',
        prefix: '● ',
        delay: 2800,
      },
      {
        text: ' Session expires in 30 minutes. No signup needed.',
        color: 'hsl(var(--muted-foreground))',
        prefix: '  ',
        delay: 4200,
      },
      {
        text: ' ────────────────────────────────────────────',
        color: 'hsl(var(--muted-foreground))',
        prefix: '',
        delay: 5800,
      },
      {
        text: '   >>> POST /webhooks/stripe (charge.succeeded)',
        color: 'hsl(152, 60%, 45%)',
        prefix: '',
        delay: 6200,
      },
      {
        text: '   <<< 200  12ms',
        color: 'hsl(100, 50%, 50%)',
        prefix: '',
        delay: 7000,
      },
      {
        text: ' ────────────────────────────────────────────',
        color: 'hsl(var(--muted-foreground))',
        prefix: '',
        delay: 8200,
      },
      {
        text: '   >>> POST /webhooks/stripe (invoice.paid)',
        color: 'hsl(152, 60%, 45%)',
        prefix: '',
        delay: 8600,
      },
      {
        text: '   <<< 200   8ms',
        color: 'hsl(100, 50%, 50%)',
        prefix: '',
        delay: 9400,
      },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      <SEO
        title="Hooklink - Local Webhook Development Environment"
        description="Never miss a webhook during development. Every event is saved, replayable, and routed to the right service. Permanent URLs, offline buffering, one-click replay. Free up to 12,500 requests/week."
        url="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Hooklink',
          description:
            'Local webhook development environment. Every webhook is saved, replayable, and routed to the right service, whether your laptop is connected or not.',
          applicationCategory: 'DeveloperApplication',
          operatingSystem: 'Any',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
          },
        }}
      />
      <PublicNav />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden landing-grain">
        {/* Background orbs */}
        <div className="absolute top-[-15%] left-[15%] w-[80vw] max-w-[500px] h-[80vw] max-h-[500px] rounded-full bg-primary/[0.04] blur-[120px] landing-mesh-orb" />
        <div className="absolute bottom-[-20%] right-[10%] w-[70vw] max-w-[400px] h-[70vw] max-h-[400px] rounded-full bg-primary/[0.06] blur-[100px] landing-mesh-orb-alt" />
        {/* Grid overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(hsl(var(--border)/0.15)_1px,transparent_1px),linear-gradient(90deg,hsl(var(--border)/0.15)_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_70%_50%_at_50%_40%,black_10%,transparent_100%)]" />

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-20 sm:pb-28">
          {/* Top badge */}
          <Reveal>
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-primary/20 bg-primary/[0.05] text-xs text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                Your webhooks have a 100% delivery rate, even when your laptop
                doesn't.
              </div>
            </div>
          </Reveal>

          {/* Headline */}
          <Reveal delay={80}>
            <h1 className="font-display text-center text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-[-0.035em] leading-[0.95] mb-6">
              <span className="block">
                Stop re-triggering{' '}
                <span className="text-primary">webhooks</span>
              </span>
              <span className="block mt-1 sm:mt-2">
                you <span className="text-muted-foreground">missed</span>
              </span>
            </h1>
          </Reveal>

          {/* Subhead */}
          <Reveal delay={160}>
            <p className="text-center text-base sm:text-lg text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed">
              Every webhook is saved, replayable, and routed to the right
              service, whether your laptop is connected or not. Permanent URLs,
              one CLI command.
            </p>
          </Reveal>

          {/* Primary CTA: Two trial commands */}
          <Reveal delay={240}>
            <div className="flex flex-col items-center gap-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative group">
                  <div className="absolute -inset-1 bg-primary/20 blur-lg rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative flex items-center gap-3 px-5 py-3 rounded-xl bg-card border border-primary/30 shadow-lg shadow-black/10">
                    <Webhook className="h-4 w-4 text-primary" />
                    <code className="font-mono-alt text-sm text-foreground">
                      npx @hooklink/cli listen 3000
                    </code>
                  </div>
                </div>
                <div className="relative group">
                  <div className="absolute -inset-1 bg-primary/20 blur-lg rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative flex items-center gap-3 px-5 py-3 rounded-xl bg-card border border-border/60 shadow-lg shadow-black/10">
                    <Globe className="h-4 w-4 text-blue-400" />
                    <code className="font-mono-alt text-sm text-foreground">
                      npx @hooklink/cli tunnel 3000
                    </code>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground/60">
                30-minute session. No account, no config, no credit card.
              </p>
            </div>
          </Reveal>

          {/* Secondary CTAs */}
          <Reveal delay={300}>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center">
              {loading ? (
                <div className="h-10 w-44 shimmer rounded-xl" />
              ) : user ? (
                <Link
                  to="/dashboard"
                  className="group inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:shadow-[0_0_32px_hsl(var(--primary)/0.4)] transition-all duration-300"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium text-sm border border-border/80 hover:border-primary/30 hover:bg-primary/[0.03] transition-all duration-200 text-muted-foreground hover:text-foreground"
                  >
                    Or create a free account for permanent URLs
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </>
              )}
              <Link
                to="https://www.producthunt.com/products/hooklink?embed=true&amp;utm_source=embed&amp;utm_medium=post_embed"
                target="_blank"
                rel="noopener"
                className="group inline-flex items-center px-6 bg-[#FF6154] dark:bg-black dark:border dark:border-primary/20 pl-3 gap-1 py-2 text-white rounded-xl font-semibold text-sm hover:shadow-[0_0_32px_hsl(var(--primary)/0.4)] transition-all duration-300"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 40 40"
                >
                  <g fill="none" fillRule="evenodd">
                    <path
                      fill={'#FFF'}
                      d="M22.667 20H17v-6h5.667a3 3 0 0 1 0 6m0-10H13v20h4v-6h5.667a7 7 0 1 0 0-14"
                    ></path>
                  </g>
                </svg>
                Check it out on Product Hunt →
              </Link>
            </div>
          </Reveal>

          {/* Terminal + Diagram side by side on larger screens */}
          <div className="mt-16 sm:mt-20 grid lg:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
            {/* Terminal */}
            <Reveal delay={350} direction="left">
              <div className="relative">
                {/* Glow behind terminal */}
                <div className="absolute -inset-4 bg-primary/[0.03] blur-3xl rounded-3xl" />
                <div className="relative glass-card overflow-hidden shadow-2xl shadow-black/20">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-border/40">
                    <div className="flex gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                      <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
                      <span className="h-2.5 w-2.5 rounded-full bg-green-500/70" />
                    </div>
                    <span className="ml-2 text-[11px] text-muted-foreground/60 flex items-center gap-1.5">
                      <Terminal className="h-3 w-3" />
                      hooklink-cli{' '}
                      {cliVersion && (
                        <span className="text-primary/50">v{cliVersion}</span>
                      )}
                    </span>
                  </div>
                  <div className="p-5 bg-background/40">
                    <Typewriter
                      lines={terminalLines}
                      className="font-mono-alt leading-loose text-muted-foreground"
                    />
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Connection diagram */}
            <Reveal delay={450} direction="right">
              <ConnectionDiagram />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Metrics ticker ── */}
      <div className="border-y border-border/50 bg-card/30 py-5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="relative overflow-hidden"
            style={{
              maskImage:
                'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
            }}
          >
            <div
              className="flex landing-ticker whitespace-nowrap"
              style={{ width: 'max-content' }}
            >
              {[0, 1].map((set) => (
                <div
                  key={set}
                  className="flex items-center gap-6 sm:gap-14 px-4 sm:px-7"
                >
                  {[
                    { label: 'Latency', value: '<50ms', icon: Zap },
                    { label: 'Uptime', value: '99.9%', icon: Activity },
                    { label: 'Encryption', value: 'TLS 1.3', icon: Lock },
                    { label: 'Protocol', value: 'WebSocket', icon: Globe },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={`${set}-${item.label}`}
                        className="flex items-center gap-3 text-sm"
                      >
                        <Icon className="h-3.5 w-3.5 text-primary/60" />
                        <span className="text-muted-foreground/60 text-xs">
                          {item.label}
                        </span>
                        <span className="font-semibold text-foreground/80 text-xs">
                          {item.value}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Provider Logos ── */}
      <section className="py-10 sm:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-center text-xs text-muted-foreground/50 uppercase tracking-[0.15em] mb-6">
              Works with your favorite tools
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12">
              {[
                { name: 'Stripe', provider: 'stripe' },
                { name: 'GitHub', provider: 'github' },
                { name: 'Shopify', provider: 'shopify' },
                { name: 'Slack', provider: 'slack' },
                { name: 'Discord', provider: 'discord' },
                { name: 'PayPal', provider: 'paypal' },
                { name: 'Twilio', provider: 'twilio' },
                { name: 'SendGrid', provider: 'sendgrid' },
                { name: 'Zoom', provider: 'zoom' },
                { name: 'Calendly', provider: 'calendly' },
              ].map((item) => (
                <div
                  key={item.provider}
                  className="flex items-center gap-2 text-muted-foreground/40 grayscale hover:grayscale-0 hover:text-foreground transition-all duration-300"
                  title={item.name}
                >
                  <ProviderIcon provider={item.provider} className="h-5 w-5" />
                  <span className="text-xs hidden sm:inline">{item.name}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Is Hooklink for you? ── */}
      <section className="py-20 sm:py-24 border-t border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.2em] text-primary mb-4">
                Sound familiar?
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.03em]">
                Hooklink is for you if&hellip;
              </h2>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 max-w-2xl mx-auto">
            {[
              "You've lost webhook events because your dev machine was offline",
              'You test Stripe, GitHub, or Shopify webhooks regularly',
              'You spend time re-triggering events just to test a single handler change',
              'You wish you could replay a failed webhook without re-triggering it',
              'You manually copy-paste curl commands to simulate webhook payloads',
              'You juggle multiple webhook providers across different local services',
            ].map((text, i) => (
              <Reveal key={text.slice(0, 30)} delay={i * 60}>
                <div className="flex items-start gap-3 py-2">
                  <div className="mt-0.5 h-5 w-5 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── What makes Hooklink different ── */}
      <section className="py-20 sm:py-24 border-t border-border/50 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] max-w-[500px] h-[40vw] max-h-[250px] bg-primary/[0.03] blur-[100px] rounded-full" />

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.2em] text-primary mb-4">
                Why Hooklink?
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.03em]">
                Webhook infrastructure, not just a tunnel
              </h2>
              <p className="text-muted-foreground mt-4 max-w-lg mx-auto">
                Forwarding is table stakes. Hooklink is a complete development
                environment for webhooks.
              </p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {[
              {
                icon: RefreshCw,
                label: "Webhooks don't wait for you",
                desc: 'Most tools drop webhooks when you disconnect. Hooklink buffers them and flushes automatically when you reconnect. Nothing gets lost.',
              },
              {
                icon: RotateCcw,
                label: 'Replay without re-triggering',
                desc: 'Debug a payment event from 3 hours ago with one click. No need to create another test transaction, push another commit, or ask QA to try again.',
              },
              {
                icon: Globe,
                label: 'One URL, forever',
                desc: 'Your webhook URL never changes between sessions, restarts, or reconnects. Configure it once in your provider dashboard and forget about it.',
              },
            ].map((item, i) => (
              <Reveal key={item.label} delay={i * 100}>
                <div className="glass-card p-6 h-full hover:border-primary/20 transition-all duration-300">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 border border-primary/10 flex items-center justify-center mb-4">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-display font-semibold text-sm mb-2">
                    {item.label}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features by workflow stage ── */}
      <section id="features" className="py-20 sm:py-24 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-14">
              <p className="text-xs uppercase tracking-[0.2em] text-primary mb-4">
                Your webhook lifecycle
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.03em]">
                Before, during, and after the request
              </h2>
            </div>
          </Reveal>

          <div className="max-w-5xl mx-auto space-y-10">
            {/* Before: receive */}
            <Reveal>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-primary/70 mb-4 flex items-center gap-2">
                  <span className="h-px w-4 bg-primary/30" />
                  Before it hits your code
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  {[
                    {
                      icon: Globe,
                      title: 'Permanent URLs',
                      desc: 'Your webhook URL survives restarts, reconnects, and laptop reboots. Configure once, forget forever.',
                    },
                    {
                      icon: Shield,
                      title: 'Source validation',
                      desc: 'IP allowlists, domain restrictions, and HMAC signature verification per endpoint. Reject bad traffic early.',
                    },
                    {
                      icon: Activity,
                      title: 'Event filtering',
                      desc: 'Only forward the events you care about. Filter by header, body field, or event type with AND/OR rules.',
                    },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <Reveal key={item.title} delay={i * 60}>
                        <div className="group glass-card p-5 h-full hover:border-primary/15 transition-all duration-300">
                          <div className="flex items-center gap-3 mb-2">
                            <Icon className="h-4 w-4 text-primary" />
                            <h3 className="font-display font-semibold text-sm tracking-[-0.01em]">
                              {item.title}
                            </h3>
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            </Reveal>

            {/* During: forward */}
            <Reveal>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-primary/70 mb-4 flex items-center gap-2">
                  <span className="h-px w-4 bg-primary/30" />
                  While it's in flight
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  {[
                    {
                      icon: Zap,
                      title: 'Sub-50ms delivery',
                      desc: 'WebSocket-based forwarding with no polling overhead. Your local server responds as if it were public.',
                    },
                    {
                      icon: Layers,
                      title: 'Multi-target routing',
                      desc: 'One CLI session, multiple endpoints. Route Stripe to :3000, GitHub to :4000, and a preview tunnel to :5173.',
                    },
                    {
                      icon: RefreshCw,
                      title: 'Offline buffering',
                      desc: 'Disconnect mid-session and webhooks queue automatically. Reconnect and they flush in order. Nothing is dropped.',
                    },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <Reveal key={item.title} delay={i * 60}>
                        <div className="group glass-card p-5 h-full hover:border-primary/15 transition-all duration-300">
                          <div className="flex items-center gap-3 mb-2">
                            <Icon className="h-4 w-4 text-primary" />
                            <h3 className="font-display font-semibold text-sm tracking-[-0.01em]">
                              {item.title}
                            </h3>
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            </Reveal>

            {/* After: debug */}
            <Reveal>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-primary/70 mb-4 flex items-center gap-2">
                  <span className="h-px w-4 bg-primary/30" />
                  After it lands
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  {[
                    {
                      icon: BarChart3,
                      title: 'Full inspection',
                      desc: 'Every request and response logged with headers, body, status, and timing. Search and filter across your entire history.',
                    },
                    {
                      icon: RotateCcw,
                      title: 'One-click replay',
                      desc: 'Re-send any webhook from your logs. Edit the payload before replaying. Bulk replay dozens for regression testing.',
                    },
                    {
                      icon: Terminal,
                      title: 'Auto-retry DLQ',
                      desc: 'Failed deliveries go to a dead-letter queue and retry with exponential backoff. No webhook is silently lost.',
                    },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <Reveal key={item.title} delay={i * 60}>
                        <div className="group glass-card p-5 h-full hover:border-primary/15 transition-all duration-300">
                          <div className="flex items-center gap-3 mb-2">
                            <Icon className="h-4 w-4 text-primary" />
                            <h3 className="font-display font-semibold text-sm tracking-[-0.01em]">
                              {item.title}
                            </h3>
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── How it Works ── */}
      <section
        id="how-it-works"
        className="py-16 sm:py-20 border-t border-border/50 relative overflow-hidden"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-[600px] h-[50vw] max-h-[300px] bg-primary/[0.03] blur-[120px] rounded-full" />

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.2em] text-primary mb-4">
                Simple Setup
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.03em]">
                Live in under a minute
              </h2>
            </div>
          </Reveal>

          <div className="max-w-3xl mx-auto">
            <div className="grid gap-4 sm:gap-0 sm:relative">
              <div className="hidden sm:block absolute left-[21px] top-[44px] bottom-[44px] w-px bg-gradient-to-b from-primary/30 via-primary/10 to-primary/30" />

              {[
                {
                  step: '01',
                  title: 'Install and connect',
                  description: (
                    <>
                      Run{' '}
                      <code className="font-mono-alt text-xs px-2 py-0.5 rounded bg-primary/10 border border-primary/10 text-primary">
                        npx @hooklink/cli listen 3000
                      </code>
                      . You get a public URL instantly. No signup, no config
                      files.
                    </>
                  ),
                },
                {
                  step: '02',
                  title: 'Paste your URL in the provider',
                  description:
                    'Copy the URL into Stripe, GitHub, Shopify, or any service that sends webhooks. Requests arrive on localhost in real time.',
                },
                {
                  step: '03',
                  title: 'Every webhook is captured',
                  description:
                    'Inspect payloads, replay past events, and filter by type. Disconnect your laptop and reconnect later; nothing is lost.',
                },
              ].map((item, i) => (
                <Reveal key={item.step} delay={i * 100}>
                  <div className="flex gap-4 items-start sm:py-3">
                    <div className="flex-shrink-0 relative">
                      <div className="w-11 h-11 rounded-lg bg-primary/[0.08] border border-primary/15 flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary">
                          {item.step}
                        </span>
                      </div>
                    </div>
                    <div className="pt-1">
                      <h3 className="font-display font-semibold text-sm mb-1 tracking-[-0.01em]">
                        {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Webhooks + Tunnels ── */}
      <section className="py-20 sm:py-24 border-t border-border/50 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.2em] text-primary mb-4">
                Two modes, one CLI
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.03em]">
                Webhooks and tunnels in a single session
              </h2>
            </div>
          </Reveal>

          <div className="max-w-5xl mx-auto space-y-6">
            {/* Multi-target connect */}
            <Reveal>
              <div className="glass-card p-6 sm:p-8 overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-primary/10 border border-primary/10 flex items-center justify-center">
                        <Layers className="h-4 w-4 text-primary" />
                      </div>
                      <h3 className="font-display font-bold text-lg tracking-[-0.01em]">
                        Multi-target connect
                      </h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      Forward multiple endpoints through a single CLI session.
                      Stripe webhooks to port 3000, GitHub hooks to port 4000, a
                      preview tunnel to port 5173.
                    </p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {[
                        'One WebSocket connection handles all your endpoints',
                        'Mix webhooks and tunnels in the same session',
                        'Each endpoint routes to its own local service',
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2.5">
                          <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="relative">
                    <div className="rounded-xl border border-border/60 bg-background/60 overflow-hidden shadow-lg shadow-black/10">
                      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/40 bg-card/50">
                        <div className="flex gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-red-500/60" />
                          <span className="h-2 w-2 rounded-full bg-yellow-500/60" />
                          <span className="h-2 w-2 rounded-full bg-green-500/60" />
                        </div>
                        <span className="ml-2 text-[10px] text-muted-foreground/50 flex items-center gap-1.5">
                          <Terminal className="h-3 w-3" />
                          hooklink-cli
                        </span>
                      </div>
                      <div className="p-4 font-mono-alt text-[10px] sm:text-xs space-y-1.5 text-muted-foreground overflow-hidden">
                        <p>
                          <span className="text-primary">$ </span>
                          hooklink connect stripe,github,preview
                        </p>
                        <p className="text-emerald-500">
                          <span className="mr-1">●</span>
                          Connected to 3 endpoints
                        </p>
                        <div className="border-t border-border/30 pt-1.5 mt-2 space-y-1">
                          <p className="flex items-center gap-1 sm:gap-2 overflow-hidden">
                            <span className="text-primary/60 flex-shrink-0">
                              →
                            </span>
                            <span className="text-foreground/70 truncate">
                              stripe-alice.hooklink.net
                            </span>
                            <span className="text-[9px] text-muted-foreground/30 ml-auto flex-shrink-0">
                              webhook
                            </span>
                            <span className="text-muted-foreground/40 flex-shrink-0">
                              :3000
                            </span>
                          </p>
                          <p className="flex items-center gap-1 sm:gap-2 overflow-hidden">
                            <span className="text-primary/60 flex-shrink-0">
                              →
                            </span>
                            <span className="text-foreground/70 truncate">
                              github-alice.hooklink.net
                            </span>
                            <span className="text-[9px] text-muted-foreground/30 ml-auto flex-shrink-0">
                              webhook
                            </span>
                            <span className="text-muted-foreground/40 flex-shrink-0">
                              :4000
                            </span>
                          </p>
                          <p className="flex items-center gap-1 sm:gap-2 overflow-hidden">
                            <span className="text-blue-400/60 flex-shrink-0">
                              →
                            </span>
                            <span className="text-foreground/70 truncate">
                              preview-alice.hooklink.net
                            </span>
                            <span className="text-[9px] text-blue-400/40 ml-auto flex-shrink-0">
                              tunnel
                            </span>
                            <span className="text-muted-foreground/40 flex-shrink-0">
                              :5173
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Tunnel explainer */}
            <Reveal delay={100}>
              <div className="glass-card p-6 sm:p-8 overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-primary/10 border border-primary/10 flex items-center justify-center">
                        <Globe className="h-4 w-4 text-primary" />
                      </div>
                      <h3 className="font-display font-bold text-lg tracking-[-0.01em]">
                        HTTPS tunnels
                      </h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      Choose a memorable subdomain, point it at{' '}
                      <code className="font-mono-alt text-xs px-1.5 py-0.5 rounded bg-muted border border-border">
                        localhost:5173
                      </code>
                      , and Hooklink bridges every request through a persistent
                      WebSocket tunnel.
                    </p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {[
                        'Instant TLS termination, no cert management',
                        'Works for static sites, APIs, and SPAs',
                        'Share preview URLs without deploying',
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2.5">
                          <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="relative">
                    <div className="rounded-xl border border-border/60 bg-background/40 p-4 space-y-3">
                      <div>
                        <p className="text-[10px] text-muted-foreground/60 uppercase tracking-widest mb-1.5">
                          Remote URL
                        </p>
                        <div className="font-mono-alt text-xs sm:text-sm px-3 py-2 rounded-lg bg-card border border-border/60 text-foreground truncate">
                          https://preview-alice.hooklink.net
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground/60 uppercase tracking-widest mb-1.5">
                          Local Target
                        </p>
                        <div className="font-mono-alt text-xs sm:text-sm px-3 py-2 rounded-lg bg-card border border-border/60 text-foreground">
                          http://localhost:5173
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row items-center justify-between font-mono-alt text-[10px] sm:text-xs bg-primary/[0.06] border border-primary/10 text-primary px-3 sm:px-4 py-2.5 rounded-lg overflow-hidden gap-1 sm:gap-0">
                        <span className="truncate min-w-0 w-full sm:w-auto text-center sm:text-left">
                          preview-alice.hooklink.net
                        </span>
                        <div className="flex items-center gap-1.5 mx-3 flex-shrink-0 hidden sm:flex">
                          <span className="h-1 w-1 rounded-full bg-primary/50" />
                          <span className="h-1 w-4 rounded-full bg-primary/30" />
                          <span className="h-1 w-1 rounded-full bg-primary/50" />
                          <ArrowRight className="h-3 w-3" />
                        </div>
                        <ArrowRight className="h-3 w-3 sm:hidden rotate-90" />
                        <span className="flex-shrink-0">localhost:5173</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Capabilities ── */}
      <section
        id="capabilities"
        className="py-24 sm:py-32 border-t border-border/50"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs uppercase tracking-[0.2em] text-primary mb-4">
                Built for Developers
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.03em]">
                More than forwarding
              </h2>
            </div>
          </Reveal>

          {/* Featured: Webhook Replay */}
          <Reveal delay={60}>
            <div className="max-w-5xl mx-auto mb-8">
              <div className="grid lg:grid-cols-2 gap-8 glass-card p-6 sm:p-8 hover:border-primary/20 transition-all duration-300 overflow-hidden">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 border border-primary/10 flex items-center justify-center">
                      <RotateCcw className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-display font-bold text-lg tracking-[-0.01em]">
                      Webhook Replay
                    </h3>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    Re-send any webhook instantly. No curl, no Postman. Edit
                    headers and payload before replaying. Debug faster by
                    reproducing the exact request that caused the issue.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground pt-1">
                    {[
                      'One-click replay from the log viewer',
                      'Edit headers, body, and query params before re-sending',
                      'Bulk replay dozens of webhooks for regression testing',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2.5">
                        <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="relative">
                  <div className="absolute -inset-2 bg-primary/[0.02] blur-2xl rounded-2xl" />
                  <div className="relative rounded-xl border border-border/60 bg-background/60 overflow-hidden shadow-lg">
                    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/40 bg-card/50">
                      <div className="flex gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-red-500/60" />
                        <span className="h-2 w-2 rounded-full bg-yellow-500/60" />
                        <span className="h-2 w-2 rounded-full bg-green-500/60" />
                      </div>
                      <span className="text-[10px] text-muted-foreground/50 ml-1">
                        replay log
                      </span>
                    </div>
                    <div className="p-3 sm:p-4 font-mono-alt text-[10px] sm:text-[11px] space-y-2.5 text-muted-foreground overflow-hidden">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <span className="text-muted-foreground/40 hidden sm:inline">
                          14:23:01
                        </span>
                        <span className="text-amber-500/80">POST</span>
                        <span className="truncate">/webhooks/stripe</span>
                        <span className="ml-auto text-emerald-500 flex-shrink-0">
                          200
                        </span>
                        <span className="text-muted-foreground/40 flex-shrink-0">
                          12ms
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 sm:gap-2 bg-primary/[0.06] -mx-3 sm:-mx-4 px-3 sm:px-4 py-1.5 border-y border-primary/10">
                        <span className="text-muted-foreground/40 hidden sm:inline">
                          14:23:45
                        </span>
                        <span className="text-primary">REPLAY</span>
                        <span className="truncate">/webhooks/stripe</span>
                        <span className="ml-auto text-emerald-500 flex-shrink-0">
                          200
                        </span>
                        <span className="text-muted-foreground/40 flex-shrink-0">
                          9ms
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <span className="text-muted-foreground/40 hidden sm:inline">
                          14:24:12
                        </span>
                        <span className="text-primary">REPLAY</span>
                        <span className="italic text-muted-foreground/60 truncate">
                          (modified)
                        </span>
                        <span className="ml-auto text-emerald-500 flex-shrink-0">
                          200
                        </span>
                        <span className="text-muted-foreground/40 flex-shrink-0">
                          11ms
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Remaining capability cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {[
              {
                icon: BarChart3,
                title: 'Request Logs',
                desc: 'Every request and response, searchable and filterable. Inspect payloads down to the last byte.',
              },
              {
                icon: Webhook,
                title: 'Bulk Replay',
                desc: 'Select dozens of webhooks, replay them all at once. Regression test entire flows in seconds.',
              },
              {
                icon: Shield,
                title: 'Source Allowlists',
                desc: 'Lock endpoints to specific IPs or domains. Unwanted traffic never reaches your machine.',
              },
              {
                icon: Globe,
                title: 'Custom Subdomains',
                desc: 'Every endpoint gets a memorable URL you own. No random hashes, no UUID soup.',
              },
              {
                icon: Terminal,
                title: 'CLI-first',
                desc: 'One install, one login, one command to connect. Designed for developers who live in the terminal.',
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.title} delay={i * 60}>
                  <div className="group glass-card p-5 hover:border-primary/15 transition-all duration-300 h-full">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-8 w-8 rounded-lg bg-primary/[0.07] border border-primary/10 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <h3 className="font-display font-semibold text-sm tracking-[-0.01em]">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Infrastructure ── */}
      <section
        id="infrastructure"
        className="py-24 sm:py-32 border-t border-border/50 relative overflow-hidden"
      >
        <div className="absolute bottom-0 left-1/4 w-[70vw] max-w-[400px] h-[40vw] max-h-[250px] bg-primary/[0.03] blur-[100px] rounded-full" />

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs uppercase tracking-[0.2em] text-primary mb-4">
                Infrastructure
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.03em]">
                Reliable by design
              </h2>
              <p className="text-muted-foreground mt-4 max-w-lg mx-auto">
                Your traffic is protected and delivered through production-grade
                infrastructure, so you can focus on building, not firefighting.
              </p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              {
                icon: Cloud,
                title: 'Cloudflare-protected',
                desc: "All traffic passes through Cloudflare's global network: DDoS protection, WAF, and edge caching included.",
              },
              {
                icon: ShieldCheck,
                title: 'Smart rate limiting',
                desc: 'Only forwarded webhooks count against your quota. Rejected, filtered, and replayed requests are always free.',
              },
              {
                icon: RefreshCw,
                title: 'Auto-retry & buffering',
                desc: 'Disconnected? Webhooks are buffered for 60 seconds and retried automatically when your CLI reconnects.',
              },
              {
                icon: Server,
                title: 'Dedicated infrastructure',
                desc: 'Runs on dedicated European servers with NVMe storage, not shared cloud functions. Consistent sub-50ms latency.',
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.title} delay={i * 80}>
                  <div className="group glass-card p-5 hover:border-primary/15 transition-all duration-300 h-full">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-8 w-8 rounded-lg bg-primary/[0.07] border border-primary/10 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <h3 className="font-display font-semibold text-sm tracking-[-0.01em]">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section
        id="pricing"
        className="py-24 sm:py-32 border-t border-border/50 relative overflow-hidden"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-[700px] h-[50vw] max-h-[400px] bg-primary/[0.02] blur-[120px] rounded-full" />

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs uppercase tracking-[0.2em] text-primary mb-4">
                Pricing
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.03em]">
                Start free, scale as you grow
              </h2>
              <p className="text-muted-foreground mt-4 max-w-lg mx-auto">
                Every plan includes SSL, webhook forwarding, HTTP tunnels, and
                WebSocket connections. No hidden fees.
              </p>
            </div>
          </Reveal>

          {plans && plans.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
              {plans.map((plan, i) => (
                <LandingPlanCard key={plan.id} plan={plan} delay={i * 80} />
              ))}
            </div>
          )}

          <Reveal delay={400}>
            <div className="text-center mt-10">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Compare all plan details
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-24 sm:py-32 border-t border-border/50 relative landing-grain overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(hsl(var(--border)/0.1)_1px,transparent_1px),linear-gradient(90deg,hsl(var(--border)/0.1)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,black_20%,transparent_100%)]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[90vw] max-w-[500px] h-[50vw] max-h-[250px] bg-primary/[0.05] blur-[100px] rounded-full" />

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-[-0.03em] mb-5">
                {user ? 'Your dashboard awaits' : 'Stop losing webhooks today'}
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground mb-10 leading-relaxed">
                {user
                  ? 'Create endpoints, launch tunnels, and monitor all your webhook traffic.'
                  : 'One command to start. Every webhook saved, replayable, and delivered.'}
              </p>
              {loading ? (
                <div className="h-13 w-56 shimmer rounded-xl mx-auto" />
              ) : user ? (
                <Link
                  to="/dashboard"
                  className="group inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-base hover:shadow-[0_0_40px_hsl(var(--primary)/0.4)] transition-all duration-300"
                >
                  <LayoutDashboard className="h-5 w-5" />
                  Open Dashboard
                  <ArrowUpRight className="h-4 w-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </Link>
              ) : (
                <div className="flex flex-col items-center gap-5">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-card border border-primary/30 shadow-lg shadow-black/10">
                      <Webhook className="h-4 w-4 text-primary" />
                      <code className="font-mono-alt text-sm text-foreground">
                        npx @hooklink/cli listen 3000
                      </code>
                    </div>
                    <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-card border border-border/60 shadow-lg shadow-black/10">
                      <Globe className="h-4 w-4 text-blue-400" />
                      <code className="font-mono-alt text-sm text-foreground">
                        npx @hooklink/cli tunnel 3000
                      </code>
                    </div>
                  </div>
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Or create a free account for permanent URLs
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Status Strip ── */}
      <StatusStrip />

      <PublicFooter />
    </div>
  );
}

function formatPlanNumber(n: number): string {
  if (n >= 1000000) return `${n / 1000000}M`;
  if (n >= 1000) return `${n / 1000}k`;
  return String(n);
}

function formatPlanBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value % 1 === 0 ? value : value.toFixed(1)} ${units[i]}`;
}

function formatPlanPrice(cents: number): string {
  if (cents === 0) return 'Free';
  return `$${cents / 100}`;
}

function LandingPlanCard({ plan, delay }: { plan: Plan; delay: number }) {
  const isPopular = plan.id === 'team';

  return (
    <Reveal delay={delay}>
      <div
        className={`group relative glass-card p-6 flex flex-col h-full hover:border-primary/20 transition-all duration-300 landing-card-glow overflow-hidden ${isPopular ? 'border-primary/30 shadow-[0_0_30px_hsl(var(--primary)/0.1)]' : ''}`}
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-bl-full" />

        {isPopular && (
          <div className="absolute -top-px left-1/2 -translate-x-1/2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-b-lg bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.15em] font-semibold">
              <span className="h-1 w-1 rounded-full bg-primary-foreground/60 animate-pulse" />
              Popular
            </div>
          </div>
        )}

        <div className="relative">
          <div className="mb-4 mt-1">
            <h3 className="font-display text-base font-bold tracking-[-0.02em]">
              {plan.name}
            </h3>
          </div>

          <div className="mb-5 pb-5 border-b border-border/40">
            <span className="font-display text-3xl font-bold tracking-[-0.03em]">
              {formatPlanPrice(plan.price_cents)}
            </span>
            {plan.price_cents > 0 && (
              <span className="text-xs text-muted-foreground/70 ml-1">/mo</span>
            )}
          </div>

          <div className="space-y-2 mb-5 flex-1">
            <div className="flex items-center gap-2 text-xs">
              <Check className="h-3 w-3 text-primary flex-shrink-0" />
              <span className="text-muted-foreground">
                {formatPlanNumber(plan.weekly_request_limit)} requests/wk
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Check className="h-3 w-3 text-primary flex-shrink-0" />
              <span className="text-muted-foreground">
                {formatPlanBytes(plan.weekly_bandwidth_limit_bytes)}/wk
                bandwidth
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Check className="h-3 w-3 text-primary flex-shrink-0" />
              <span className="text-muted-foreground">
                {plan.max_endpoints} endpoints
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Check className="h-3 w-3 text-primary flex-shrink-0" />
              <span className="text-muted-foreground">
                {plan.log_retention_days}d log retention
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              {plan.feature_bulk_replay ? (
                <Check className="h-3 w-3 text-primary flex-shrink-0" />
              ) : (
                <X className="h-3 w-3 text-muted-foreground/30 flex-shrink-0" />
              )}
              <span
                className={
                  plan.feature_bulk_replay
                    ? 'text-muted-foreground'
                    : 'text-muted-foreground/40'
                }
              >
                Bulk replay
              </span>
            </div>
          </div>

          <Link
            to={plan.id === 'free' ? '/register' : '/pricing'}
            className={`block w-full py-2 rounded-lg text-center text-xs font-medium transition-all duration-200 ${
              isPopular
                ? 'bg-primary text-primary-foreground hover:shadow-[0_0_20px_hsl(var(--primary)/0.35)]'
                : 'border border-border/80 hover:border-primary/30 hover:bg-primary/[0.03] text-muted-foreground hover:text-foreground'
            }`}
          >
            {plan.id === 'free' ? 'Start Free' : 'Get Started'}
          </Link>
        </div>
      </div>
    </Reveal>
  );
}

function StatusStrip() {
  const { data, isLoading } = useSystemStatus(60000);

  if (isLoading || !data?.services) return null;

  const allOk = data.status === 'ok';

  return (
    <div className="border-t border-border/50 py-4">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <StatusIndicator variant={allOk ? 'online' : 'error'} />
            <span className="text-xs text-muted-foreground">
              {allOk ? 'All systems operational' : 'Some systems degraded'}
            </span>
            <div className="hidden sm:flex items-center gap-2 ml-3 pl-3 border-l border-border/50">
              {(['api', 'database', 'websocket'] as const).map((key) => (
                <div key={key} className="flex items-center gap-1.5">
                  <StatusIndicator
                    variant={
                      data.services[key].status === 'operational'
                        ? 'online'
                        : 'error'
                    }
                    className="!h-1.5 !w-1.5"
                  />
                  <span className="text-[10px] text-muted-foreground/60 capitalize">
                    {key === 'websocket' ? 'WS' : key === 'api' ? 'API' : 'DB'}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <Link
            to="/status"
            className="text-xs text-muted-foreground/80 hover:text-foreground transition-colors flex items-center gap-1"
          >
            System status
            <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
